// src/screens/Admin/EngagementGraphsDetail.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

import './EngagementGraphsDetail.css';

import backIcon from '../../assets/backspace-icon.png';
import infoIcon from '../../assets/info-icon.png';

import LineChart from '../../components/charts/LineChart';
import GraphsExplainerDrawer from '../../components/popups/GraphsExplainerDrawer';

export default function EngagementGraphsDetail() {
  const navigate = useNavigate();

  const [series, setSeries] = useState([]);
  const [days, setDays] = useState(30); // number | 'all'
  const [mode, setMode] = useState('daily');
  const [deltas, setDeltas] = useState(null);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState('avgLikesPerPost');

  const [winWidth, setWinWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    fetchData(days);
    // eslint-disable-next-line
  }, []);

  const chartWidth = Math.min(1100, Math.max(320, winWidth - 48));

  const fetchData = async (d) => {
    try {
      setDays(d);
      const res = await api.get(`/admin/metrics/engagement-timeseries?days=${d}`);
      setSeries(res.data.series || []);
      setMode(res.data.mode || 'daily');
      setDeltas(res.data.deltas || null);
    } catch (err) {
      console.error('Failed to load engagement graphs', err);
    }
  };

  const buildXY = (key) => series.map((d) => ({ x: d.date, y: d[key] }));

  const formatXShort = useMemo(() => {
    return (d) => (typeof d === 'string' ? d.slice(5) : d);
  }, []);

  const fmt2 = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return '0.00';
    return x.toFixed(2);
  };

  const openExplain = (type) => {
    setDrawerType(type);
    setDrawerOpen(true);
  };

  const InfoBtn = ({ onClick, label }) => (
    <button
      type="button"
      onClick={onClick}
      className="eng-graphs-infoBtn"
      aria-label={label}
      title={label}
    >
      <img src={infoIcon} alt="" className="eng-graphs-infoIcon" />
    </button>
  );

  const compareText =
    deltas?.windowLabel ||
    (days === 'all'
      ? (mode === 'weekly' ? 'previous 4 weeks' : 'previous 30 days')
      : `previous ${days} days`);

  const DeltaLine = ({ statKey }) => {
    const s = deltas?.[statKey];
    if (!s || deltas?.ok === false) return null;

    if (s.isNew) {
      return (
        <span className="eng-graphs-delta">
          <span className="eng-graphs-delta-pct eng-graphs-delta-new">New</span>
          <span className="eng-graphs-delta-sub"> • from {compareText}</span>
        </span>
      );
    }

    const pct = Number(s.pct);
    const isPos = Number.isFinite(pct) && pct > 0;
    const isNeg = Number.isFinite(pct) && pct < 0;

    const pctText = Number.isFinite(pct)
      ? `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`
      : '0.0%';

    return (
      <span className="eng-graphs-delta">
        <span
          className={[
            'eng-graphs-delta-pct',
            isPos ? 'eng-graphs-delta-pos' : '',
            isNeg ? 'eng-graphs-delta-neg' : '',
          ].join(' ')}
        >
          {pctText}
        </span>
        <span className="eng-graphs-delta-sub"> • from {compareText}</span>
      </span>
    );
  };

  return (
    <div className="eng-graphs-page">
      <div className="eng-graphs-header">
        <img
          src={backIcon}
          alt="Back"
          className="eng-graphs-back"
          onClick={() => navigate('/admin/graphs')}
        />
        <span className="eng-graphs-title">Engagement Graphs</span>
      </div>

      <div className="eng-graphs-scroll">
        <div className="eng-graphs-filters">
          <button className={days === 7 ? 'active' : ''} onClick={() => fetchData(7)}>7d</button>
          <button className={days === 30 ? 'active' : ''} onClick={() => fetchData(30)}>30d</button>
          <button className={days === 90 ? 'active' : ''} onClick={() => fetchData(90)}>90d</button>
          <button className={days === 'all' ? 'active' : ''} onClick={() => fetchData('all')}>All</button>
        </div>

        <div className="eng-graphs-rows">
          {/* ✅ NEW: Avg Posts / Day */}
          <LineChart
            title="Avg Posts / Day"
            subtitle={<DeltaLine statKey="avgPostsPerDay" />}
            titleRight={
              <InfoBtn
                onClick={() => openExplain('avgPostsPerDay')}
                label="What does Avg Posts / Day mean?"
              />
            }
            data={buildXY('avgPostsPerDay')}
            width={chartWidth}
            height={240}
            formatX={formatXShort}
            formatY={(v) => fmt2(v)}
            tooltipFormat={({ x, y }) => ({
              title: `Date: ${x}`,
              lines: [`Avg Posts / Day: ${fmt2(y)}`],
            })}
          />

          <LineChart
            title="Avg Likes / Post"
            subtitle={<DeltaLine statKey="avgLikesPerPost" />}
            titleRight={
              <InfoBtn
                onClick={() => openExplain('avgLikesPerPost')}
                label="What does Avg Likes / Post mean?"
              />
            }
            data={buildXY('avgLikesPerPost')}
            width={chartWidth}
            height={240}
            formatX={formatXShort}
            formatY={(v) => fmt2(v)}
            tooltipFormat={({ x, y }) => ({
              title: `Date: ${x}`,
              lines: [`Avg Likes / Post: ${fmt2(y)}`],
            })}
          />

          <LineChart
            title="Avg Comments / Post"
            subtitle={<DeltaLine statKey="avgCommentsPerPost" />}
            titleRight={
              <InfoBtn
                onClick={() => openExplain('avgCommentsPerPost')}
                label="What does Avg Comments / Post mean?"
              />
            }
            data={buildXY('avgCommentsPerPost')}
            width={chartWidth}
            height={240}
            formatX={formatXShort}
            formatY={(v) => fmt2(v)}
            tooltipFormat={({ x, y }) => ({
              title: `Date: ${x}`,
              lines: [`Avg Comments / Post: ${fmt2(y)}`],
            })}
          />

          <LineChart
            title="Engagement Density (Likes + Comments) / Post"
            subtitle={<DeltaLine statKey="engagementDensity" />}
            titleRight={
              <InfoBtn
                onClick={() => openExplain('engagementDensity')}
                label="What does Engagement Density mean?"
              />
            }
            data={buildXY('engagementDensity')}
            width={chartWidth}
            height={240}
            formatX={formatXShort}
            formatY={(v) => fmt2(v)}
            tooltipFormat={({ x, y }) => ({
              title: `Date: ${x}`,
              lines: [`Engagement Density: ${fmt2(y)}`],
            })}
          />
        </div>
      </div>

      <GraphsExplainerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        type={drawerType}
      />
    </div>
  );
}
